import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clubsService } from '../../../services';
import StandClayConfigFields from './StandClayConfigFields';
import {
    buildStandClayConfig,
    buildStandType,
    buildPresentationDefaults,
    disciplineTypes,
    formatStandIdentity,
    getDefaultTargetLabelsForDiscipline
} from './standClayConfig';
import '../Clubs/ClubsDashboard.css';
import './StandSetup.css';

const AddStand = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        discipline: '',
        variant: '',
        description: '',
        standType: '',
        // Initialize with zeros instead of empty strings
        latitude: 0,
        longitude: 0
    });
    const [targetLabels, setTargetLabels] = useState(getDefaultTargetLabelsForDiscipline('Sporting'));
    const [presentationDefaults, setPresentationDefaults] = useState(
        buildPresentationDefaults(getDefaultTargetLabelsForDiscipline('Sporting'))
    );
    const [customTargetInput, setCustomTargetInput] = useState('');
    const [standNumber, setStandNumber] = useState('');
    const [standTitle, setStandTitle] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'discipline') {
            setFormData((current) => ({
                ...current,
                discipline: value,
                variant: '',
                standType: buildStandType(value, '')
            }));
            setTargetLabels(getDefaultTargetLabelsForDiscipline(value));
            setPresentationDefaults(buildPresentationDefaults(getDefaultTargetLabelsForDiscipline(value)));
            return;
        }

        if (name === 'variant') {
            setFormData((current) => ({
                ...current,
                variant: value,
                standType: buildStandType(current.discipline, value)
            }));
            return;
        }

        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        const displayStandName = formatStandIdentity(standNumber, standTitle, formData.name);

        if (!displayStandName || !formData.discipline || !formData.variant) {
            const errorMsg = 'Please fill a stand name, discipline, and variant.';
            setError(errorMsg);

            // Use toast for validation errors as well
            if (window.toast) {
                window.toast.error(errorMsg);
            }

            setIsLoading(false);
            return;
        }

        if (targetLabels.length === 0) {
            const errorMsg = 'Add at least one clay label for this stand.';
            setError(errorMsg);
            if (window.toast) {
                window.toast.error(errorMsg);
            }
            setIsLoading(false);
            return;
        }

        try {
            // Prepare stand data with proper type conversions
            const standData = {
                ...formData,
                name: displayStandName,
                clubId: parseInt(clubId, 10),
                standType: buildStandType(formData.discipline, formData.variant),
                clayConfigJson: buildStandClayConfig({
                    standName: displayStandName,
                    discipline: formData.discipline,
                    variant: formData.variant,
                    targetLabels,
                    presentationDefaults
                }),
                // Convert latitude and longitude to numbers or use null for empty values
                latitude: formData.latitude ? parseFloat(formData.latitude) : 0,
                longitude: formData.longitude ? parseFloat(formData.longitude) : 0
            };

            console.log('Submitting stand data:', standData);

            const data = await clubsService.createStand(standData);
            console.log('Stand created successfully:', data);

            // Display toast notification instead of alert
            if (window.toast) {
                window.toast.success('Stand created successfully!');
            }
            navigate(`/dashboard/club/${clubId}/stands`);
        } catch (err) {
            console.error('Error creating stand:', err);

            // Display toast error notification instead of just setting error state
            if (window.toast) {
                window.toast.error(`Failed to create stand: ${err.message}`);
            }

            setError(`Failed to create stand: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(`/dashboard/club/${clubId}/stands`);
    };

    const displayStandName = formatStandIdentity(standNumber, standTitle, formData.name);

    return (
        <div className="dashboard-component">
            <h1 className="mb-4">Add New Stand</h1>

            {error && (
                <div className="error-message" role="alert">
                    <p>{error}</p>
                </div>
            )}

            <div className="dashboard-card p-4">
                <form className="dashboard-form" onSubmit={handleSubmit}>
                    <div className="dashboard-form-grid">
                        <div className="form-group">
                            <label>Stand Number</label>
                            <input
                                type="number"
                                min="1"
                                value={standNumber}
                                onChange={(e) => setStandNumber(e.target.value)}
                                placeholder="4"
                            />
                            <p className="form-helper-text">Optional. Use this when your ground uses numbered pegs or QR signs.</p>
                        </div>

                        <div className="form-group">
                            <label>Stand Name / Title</label>
                            <input
                                type="text"
                                value={standTitle}
                                onChange={(e) => setStandTitle(e.target.value)}
                                placeholder="Rabbits, English Skeet, Left / Right Quarter Away"
                                required
                            />
                            <p className="form-helper-text">Required. This can be a nickname, layout name, or presentation title if the stand is not numbered.</p>
                        </div>

                        <div className="form-group col-span-2">
                            <label>Presentation Summary / Tips</label>
                            <textarea
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="E.g., Pair of quick rabbits left to right then right to left"
                            />
                            <p className="form-helper-text">Use this for a short coaching note or quick reminder of what the stand throws.</p>
                        </div>

                        <div className="form-group col-span-2">
                            <div className="stand-identity-preview">
                                <span className="stand-identity-preview-label">Preview</span>
                                <strong>{displayStandName || 'Stand name will appear here'}</strong>
                                <p>Numbered courses and named layouts both work here, so venues can mirror the way their ground is actually signed.</p>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Discipline</label>
                            <select
                                name="discipline"
                                value={formData.discipline}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Discipline</option>
                                {Object.keys(disciplineTypes).map((d) => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>

                        {formData.discipline && (
                            <div className="form-group">
                                <label>Variant</label>
                                <select
                                    name="variant"
                                    value={formData.variant}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Variant</option>
                                    {disciplineTypes[formData.discipline].map((v) => (
                                        <option key={v} value={v}>{v}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {formData.discipline && (
                            <StandClayConfigFields
                                discipline={formData.discipline}
                                targetLabels={targetLabels}
                                presentationDefaults={presentationDefaults}
                                customTargetInput={customTargetInput}
                                onCustomTargetInputChange={setCustomTargetInput}
                                onTargetLabelsChange={setTargetLabels}
                                onPresentationDefaultsChange={setPresentationDefaults}
                            />
                        )}
                    </div>

                    <div className="form-buttons mt-4">
                        <button
                            type="submit"
                            className="dashboard-card-default-btn me-3"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Saving...' : 'Save Stand'}
                        </button>
                        <button
                            type="button"
                            className="dashboard-card-outline-btn"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddStand;
